import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Clock, 
  Code, 
  ExternalLink,
  FileText,
  Layout,
  Database,
  Megaphone,
  Mail,
  X,
  Copy,
  Check,
  LogOut,
  LayoutDashboard,
  RefreshCw,
  Trash2,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Download,
  FileSignature
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Page definitions for sitemap
const pages = [
  {
    title: 'Partner Application',
    description: 'Sign up to become a Quoteplicity partner.',
    path: '/partner',
    embedPath: '/embed',
    icon: Users,
    color: 'bg-[#10c0a5]',
    supportsPostMessage: true,
  },
  {
    title: 'Waitlist Signup',
    description: 'Full waitlist page with header and footer.',
    path: '/waitlist',
    embedPath: '/waitlist',
    icon: Clock,
    color: 'bg-amber-500',
    supportsPostMessage: false,
  },
  {
    title: 'Waitlist Form (Embed)',
    description: 'Original pricing: Basic, Starter, Professional, Agency.',
    path: '/waitlist/embed',
    embedPath: '/waitlist/embed',
    icon: FileText,
    color: 'bg-orange-500',
    supportsPostMessage: true,
  },
  {
    title: 'Waitlist Form V2 (Embed)',
    description: 'New pricing: Producer ($59), Agency ($199), Enterprise.',
    path: '/waitlist/embed/v2',
    embedPath: '/waitlist/embed/v2',
    icon: FileText,
    color: 'bg-orange-600',
    supportsPostMessage: true,
  },
  {
    title: 'Marketing Agency',
    description: 'Full marketing services page.',
    path: '/marketing',
    embedPath: '/marketing',
    icon: Megaphone,
    color: 'bg-purple-500',
    supportsPostMessage: false,
  },
  {
    title: 'Marketing Form (Embed)',
    description: 'Standalone qualification questionnaire.',
    path: '/marketing/embed',
    embedPath: '/marketing/embed',
    icon: FileText,
    color: 'bg-emerald-500',
    supportsPostMessage: true,
  },
  {
    title: 'Ethos Contracting',
    description: 'Ethos contracting application form. Saved to database and forwarded to Zapier.',
    path: '/contracting/ethos/apply',
    embedPath: '/contracting/ethos/apply',
    icon: FileSignature,
    color: 'bg-emerald-600',
    supportsPostMessage: true,
  },
];

// Embed Modal Component
const EmbedModal = ({ page, baseUrl, onClose }) => {
  const [copied, setCopied] = useState(false);
  
  const generateEmbedCode = () => {
    if (page.supportsPostMessage) {
      return `<!-- ${page.title} Embed -->
<iframe 
  id="quoteplicity-${page.embedPath.replace(/\//g, '-').replace(/^-/, '')}"
  src="${baseUrl}${page.embedPath}" 
  width="100%" 
  frameborder="0" 
  scrolling="no"
  style="border: none; overflow: hidden;"
></iframe>

<script>
  window.addEventListener('message', function(event) {
    var iframe = document.getElementById('quoteplicity-${page.embedPath.replace(/\//g, '-').replace(/^-/, '')}');
    if (!iframe) return;

    if (event.data && event.data.type === 'resize') {
      iframe.style.height = event.data.height + 'px';
    }

    if (event.data && event.data.type === 'scrollToTop') {
      iframe.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
</script>`;
    } else {
      return `<!-- ${page.title} Embed -->
<iframe 
  id="quoteplicity-${page.embedPath.replace(/\//g, '-').replace(/^-/, '')}"
  src="${baseUrl}${page.embedPath}" 
  width="100%" 
  frameborder="0" 
  style="border: none; min-height: 800px;"
></iframe>`;
    }
  };

  const embedCode = generateEmbedCode();

  const copyToClipboard = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-6 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className={`${page.color} p-2 rounded-lg text-white`}>
                <Code className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">Embed {page.title}</h2>
                <p className="text-sm text-slate-500">Copy the code below</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
            <div className="mb-4 flex justify-end">
              <button
                onClick={copyToClipboard}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  copied ? 'bg-green-500 text-white' : 'bg-[#10c0a5] hover:bg-[#0ea892] text-white'
                }`}
              >
                {copied ? <><Check className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy Code</>}
              </button>
            </div>
            <div className="bg-slate-900 rounded-xl p-4 overflow-x-auto">
              <pre className="text-sm text-slate-300 whitespace-pre-wrap">{embedCode}</pre>
            </div>

            {page.supportsPostMessage && (
              <div className="mt-4 bg-slate-50 border border-slate-200 rounded-xl p-4">
                <h4 className="font-medium text-slate-900 mb-2">How it works:</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li><strong>resize</strong> — Auto-adjusts iframe height</li>
                  <li><strong>scrollToTop</strong> — Scrolls to iframe on step change</li>
                </ul>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 bg-slate-50">
            <a
              href={page.path}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-white"
            >
              <ExternalLink className="w-4 h-4" /> Preview
            </a>
            <button onClick={onClose} className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-medium">
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const AdminDashboard = () => {
  const { user, logout, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('forms');
  const [embedModalPage, setEmbedModalPage] = useState(null);
  const baseUrl = window.location.origin;

  // Submissions state
  const [partners, setPartners] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [marketing, setMarketing] = useState([]);
  const [ethos, setEthos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [retrying, setRetrying] = useState({});

  // Add noindex meta tag
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Fetch submissions
  useEffect(() => {
    if (activeTab === 'submissions' && isAuthenticated) {
      fetchSubmissions();
    }
  }, [activeTab, isAuthenticated]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const [partnersRes, waitlistRes, marketingRes, ethosRes] = await Promise.all([
        axios.get(`${API}/partners`),
        axios.get(`${API}/waitlist`),
        axios.get(`${API}/marketing-inquiry`),
        axios.get(`${API}/ethos-contracting`, { headers: { 'X-Admin-Token': localStorage.getItem('admin_token') } }),
      ]);
      setPartners(partnersRes.data || []);
      setWaitlist(waitlistRes.data || []);
      setMarketing(marketingRes.data || []);
      setEthos(ethosRes.data || []);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const [exportingCsv, setExportingCsv] = useState(false);

  const handleExportWaitlistCsv = async () => {
    setExportingCsv(true);
    try {
      const response = await axios.get(`${API}/waitlist/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `waitlist-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting waitlist CSV:', error);
      alert('Failed to export waitlist CSV');
    } finally {
      setExportingCsv(false);
    }
  };

  const handleRetryZapier = async (submissionId, type) => {
    setRetrying(prev => ({ ...prev, [submissionId]: true }));
    try {
      await axios.post(`${API}/retry-zapier`, { id: submissionId, type });
      await fetchSubmissions();
    } catch (error) {
      console.error('Error retrying Zapier:', error);
      alert('Failed to retry Zapier submission');
    } finally {
      setRetrying(prev => ({ ...prev, [submissionId]: false }));
    }
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#10c0a5]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const renderSubmissionRow = (item, type) => {
    const isExpanded = expandedRows[item.id];
    const isRetrying = retrying[item.id];

    return (
      <React.Fragment key={item.id}>
        <tr className="border-b border-slate-100 hover:bg-slate-50">
          <td className="px-4 py-3">
            <button onClick={() => toggleRow(item.id)} className="text-slate-500 hover:text-slate-700">
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          </td>
          <td className="px-4 py-3 font-medium text-slate-900">{item.name || `${item.first_name || ''} ${item.last_name || ''}`.trim()}</td>
          <td className="px-4 py-3 text-slate-600">{item.email}</td>
          <td className="px-4 py-3 text-slate-600">{item.company_name || item.agency_name}</td>
          <td className="px-4 py-3">
            {item.zapier_sent === true ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                <CheckCircle2 className="w-3 h-3" /> Sent
              </span>
            ) : item.zapier_sent === false ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">
                <AlertCircle className="w-3 h-3" /> Failed
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs">
                Pending
              </span>
            )}
          </td>
          <td className="px-4 py-3">
            <button
              onClick={() => handleRetryZapier(item.id, type)}
              disabled={isRetrying}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-[#10c0a5] hover:bg-[#e6faf7] rounded"
            >
              <RefreshCw className={`w-3 h-3 ${isRetrying ? 'animate-spin' : ''}`} />
              Retry
            </button>
          </td>
        </tr>
        {isExpanded && (
          <tr className="bg-slate-50">
            <td colSpan="6" className="px-4 py-4">
              <pre className="text-xs text-slate-600 overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(item, null, 2)}
              </pre>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <LayoutDashboard className="w-6 h-6 text-[#10c0a5]" />
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Admin Dashboard
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">Welcome, {user?.username}</span>
              <button
                onClick={handleLogout}
                className="inline-flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-8">
            <button
              onClick={() => setActiveTab('forms')}
              className={`py-4 border-b-2 font-medium transition-colors ${
                activeTab === 'forms'
                  ? 'border-[#10c0a5] text-[#10c0a5]'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <Layout className="w-4 h-4" />
                Forms & Embeds
              </span>
            </button>
            <button
              onClick={() => setActiveTab('submissions')}
              className={`py-4 border-b-2 font-medium transition-colors ${
                activeTab === 'submissions'
                  ? 'border-[#10c0a5] text-[#10c0a5]'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <Database className="w-4 h-4" />
                Submissions
              </span>
            </button>
            <button
              onClick={() => setActiveTab('email')}
              className={`py-4 border-b-2 font-medium transition-colors ${
                activeTab === 'email'
                  ? 'border-[#10c0a5] text-[#10c0a5]'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <span className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email Templates
              </span>
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Forms & Embeds Tab */}
        {activeTab === 'forms' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Available Forms</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pages.map((page) => {
                const Icon = page.icon;
                return (
                  <div
                    key={page.path}
                    className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`${page.color} p-2 rounded-lg text-white`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">{page.title}</h3>
                        <p className="text-sm text-slate-500 mt-1">{page.description}</p>
                      </div>
                    </div>
                    {page.supportsPostMessage && (
                      <span className="inline-block px-2 py-1 bg-green-100 text-green-700 rounded text-xs mb-3">
                        Auto-resize
                      </span>
                    )}
                    <div className="flex gap-2 mt-3">
                      <a
                        href={page.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 bg-[#10c0a5] text-white rounded-lg text-sm font-medium hover:bg-[#0ea892]"
                      >
                        <ExternalLink className="w-3 h-3" /> View
                      </a>
                      <button
                        onClick={() => setEmbedModalPage(page)}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 border border-[#10c0a5] text-[#10c0a5] rounded-lg text-sm font-medium hover:bg-[#e6faf7]"
                      >
                        <Code className="w-3 h-3" /> Embed
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Submissions Tab */}
        {activeTab === 'submissions' && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">All Submissions</h2>
              <button
                onClick={fetchSubmissions}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#10c0a5] text-white rounded-lg font-medium hover:bg-[#0ea892] disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#10c0a5]" />
              </div>
            ) : (
              <>
                {/* Partners */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#10c0a5]" />
                      Partner Applications ({partners.length})
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-2 text-left w-10"></th>
                          <th className="px-4 py-2 text-left text-slate-600 font-medium">Name</th>
                          <th className="px-4 py-2 text-left text-slate-600 font-medium">Email</th>
                          <th className="px-4 py-2 text-left text-slate-600 font-medium">Company</th>
                          <th className="px-4 py-2 text-left text-slate-600 font-medium">Zapier</th>
                          <th className="px-4 py-2 text-left text-slate-600 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partners.length === 0 ? (
                          <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-500">No submissions yet</td></tr>
                        ) : (
                          partners.map(item => renderSubmissionRow(item, 'partner'))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Waitlist */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-amber-500" />
                      Waitlist Signups ({waitlist.length})
                    </h3>
                    <button
                      onClick={handleExportWaitlistCsv}
                      disabled={exportingCsv}
                      data-testid="export-waitlist-csv-btn"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#10c0a5] text-[#10c0a5] rounded-lg text-xs font-medium hover:bg-[#e6faf7] disabled:opacity-50"
                    >
                      {exportingCsv ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                      Export CSV
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-2 text-left w-10"></th>
                          <th className="px-4 py-2 text-left text-slate-600 font-medium">Name</th>
                          <th className="px-4 py-2 text-left text-slate-600 font-medium">Email</th>
                          <th className="px-4 py-2 text-left text-slate-600 font-medium">Company</th>
                          <th className="px-4 py-2 text-left text-slate-600 font-medium">Zapier</th>
                          <th className="px-4 py-2 text-left text-slate-600 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {waitlist.length === 0 ? (
                          <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-500">No submissions yet</td></tr>
                        ) : (
                          waitlist.map(item => renderSubmissionRow(item, 'waitlist'))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Marketing */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <Megaphone className="w-4 h-4 text-purple-500" />
                      Marketing Inquiries ({marketing.length})
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-2 text-left w-10"></th>
                          <th className="px-4 py-2 text-left text-slate-600 font-medium">Name</th>
                          <th className="px-4 py-2 text-left text-slate-600 font-medium">Email</th>
                          <th className="px-4 py-2 text-left text-slate-600 font-medium">Company</th>
                          <th className="px-4 py-2 text-left text-slate-600 font-medium">Zapier</th>
                          <th className="px-4 py-2 text-left text-slate-600 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {marketing.length === 0 ? (
                          <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-500">No submissions yet</td></tr>
                        ) : (
                          marketing.map(item => renderSubmissionRow(item, 'marketing'))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Ethos Contracting */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="ethos-submissions-section">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <FileSignature className="w-4 h-4 text-emerald-600" />
                      Ethos Contracting Applications ({ethos.length})
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-2 text-left w-10"></th>
                          <th className="px-4 py-2 text-left text-slate-600 font-medium">Name</th>
                          <th className="px-4 py-2 text-left text-slate-600 font-medium">Email</th>
                          <th className="px-4 py-2 text-left text-slate-600 font-medium">Company</th>
                          <th className="px-4 py-2 text-left text-slate-600 font-medium">Zapier</th>
                          <th className="px-4 py-2 text-left text-slate-600 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ethos.length === 0 ? (
                          <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-500">No submissions yet</td></tr>
                        ) : (
                          ethos.map(item => renderSubmissionRow(item, 'ethos'))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Email Templates Tab */}
        {activeTab === 'email' && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-slate-900">Email Templates</h2>
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-start gap-4">
                <div className="bg-rose-500 p-3 rounded-xl text-white">
                  <Mail className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Marketing Confirmation Email</h3>
                  <p className="text-slate-600 mb-4">
                    HTML email templates for GoHighLevel automation. Ready to use with contact variables.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="/email-templates/marketing-confirmation.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#10c0a5] text-white rounded-lg font-medium hover:bg-[#0ea892]"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Version 1
                    </a>
                    <a
                      href="/email-templates/marketing-confirmation-v2.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#10c0a5] text-[#10c0a5] rounded-lg font-medium hover:bg-[#e6faf7]"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Version 2
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Embed Modal */}
      {embedModalPage && (
        <EmbedModal
          page={embedModalPage}
          baseUrl={baseUrl}
          onClose={() => setEmbedModalPage(null)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
